<template>
  <base-modal wide :show="showModal" @controleModalApperance="$emit('closeModal')">
    <template #modal>
      <div class="modal_card">
        <div class="modal_header">
          <div>
            <h5>{{ $t("complaints.detail") }} #{{ detail && detail.id }}</h5>
            <span
              v-if="detail"
              class="status_badge"
              :class="statusClass(detail.status)"
            >
              {{ label(detail.status) }}
            </span>
          </div>
          <button class="close_btn" @click="$emit('closeModal')">
            <i class="fal fa-times"></i>
          </button>
        </div>

        <div class="modal_body" v-if="detail">
          <section class="info_section">
            <h6 class="section_title">{{ $t("complaints.complaintInfo") }}</h6>
            <div class="facts">
              <div>
                <span>{{ $t("complaints.subject") }}</span>
                <strong>{{ detail.subject || "-" }}</strong>
              </div>
              <div>
                <span>{{ $t("complaints.type") }}</span>
                <strong>{{ label(detail.type) }}</strong>
              </div>
              <div>
                <span>{{ $t("complaints.submissionDate") }}</span>
                <strong>{{ detail.created_at || "-" }}</strong>
              </div>
              <div>
                <span>{{ $t("complaints.validated") }}</span>
                <strong>{{ detail.validated_at || "-" }}</strong>
              </div>
            </div>
            <p class="description" v-if="detail.description">
              {{ descriptionText }}
            </p>
            <button
              v-if="!detail.validated_at"
              class="action_btn"
              :disabled="isValidating"
              @click="validateComplaint"
            >
              {{ $t("complaints.validate") }}
            </button>
          </section>

          <section class="info_section" v-if="booking">
            <h6 class="section_title">{{ $t("complaints.bookingInfo") }}</h6>
            <div class="facts">
              <div>
                <span>{{ $t("complaints.bookingNumber") }}</span>
                <strong>{{ booking.booking_number || booking.id }}</strong>
              </div>
              <div>
                <span>{{ $t("complaints.status") }}</span>
                <strong>{{ label(booking.status) }}</strong>
              </div>
              <div>
                <span>{{ $t("complaints.paymentType") }}</span>
                <strong>{{ label(booking.payment_type) }}</strong>
              </div>
              <div>
                <span>{{ $t("complaints.paymentMethod") }}</span>
                <strong>{{ booking.payment_method || "-" }}</strong>
              </div>
              <div>
                <span>{{ $t("complaints.remainingPayment") }}</span>
                <strong>{{ booking.remaining_payment_method || "-" }}</strong>
              </div>
              <div>
                <span>{{ $t("complaints.isPaid") }}</span>
                <strong>{{
                  booking.is_paid ? $t("complaints.paid") : $t("complaints.unpaid")
                }}</strong>
              </div>
            </div>
            <div class="facts" v-if="booking.dates">
              <div>
                <span>{{ $t("complaints.schedule") }}</span>
                <strong dir="ltr">
                  {{ booking.dates.start_date }} {{ booking.dates.start_time }}
                  —
                  {{ booking.dates.end_date }} {{ booking.dates.end_time }}
                </strong>
              </div>
            </div>
            <div class="amount_grid" v-if="amountItems.length">
              <div v-for="item in amountItems" :key="item.key">
                <span>{{ item.label }}</span>
                <strong>{{ item.value }}</strong>
              </div>
            </div>
          </section>

          <section class="info_section" v-if="payout">
            <h6 class="section_title">{{ $t("complaints.payout") }}</h6>
            <div class="facts">
              <div>
                <span>{{ $t("complaints.status") }}</span>
                <strong>{{ payoutStatusLabel(payout.status) }}</strong>
              </div>
              <div v-if="payout.provider_id">
                <span>{{ $t("complaints.providerName") }}</span>
                <strong>#{{ payout.provider_id }}</strong>
              </div>
              <div v-for="item in payoutFacts" :key="item.label">
                <span>{{ item.label }}</span>
                <strong>{{ item.value }}</strong>
              </div>
            </div>
            <div class="people payout_parts" v-if="payout.first_part || payout.second_part">
              <article v-if="payout.first_part">
                <span>{{ $t("complaints.firstPart") }}</span>
                <strong>{{ text(payout.first_part.amount) }}</strong>
                <p>{{ payoutStatusLabel(payout.first_part.release_status) }}</p>
                <p>
                  {{ $t("complaints.delivered") }}:
                  {{ payout.first_part.delivered ? $t("complaints.yes") : $t("complaints.no") }}
                </p>
              </article>
              <article v-if="payout.second_part">
                <span>{{ $t("complaints.secondPart") }}</span>
                <strong>{{ text(payout.second_part.amount) }}</strong>
                <p>{{ payoutStatusLabel(payout.second_part.release_status) }}</p>
                <p>
                  {{ $t("complaints.delivered") }}:
                  {{ payout.second_part.delivered ? $t("complaints.yes") : $t("complaints.no") }}
                </p>
              </article>
            </div>
            <div class="releases" v-if="payoutReleases.length">
              <h6 class="section_title">{{ $t("complaints.payoutRows") }}</h6>
              <div class="log_item" v-for="row in payoutReleases" :key="row.id">
                <strong>
                  #{{ row.id }}
                  — {{ partLabel(row.part) }}
                  — {{ text(row.amount) }}
                </strong>
                <p>
                  {{ payoutStatusLabel(row.status) }}
                  <span v-if="row.applied_reason"> — {{ reasonLabel(row.applied_reason) }}</span>
                </p>
                <small>{{ row.released_at || "-" }}</small>
                <router-link :to="`/payouts/show/${row.id}`">
                  {{ $t("complaints.viewPayout") }}
                </router-link>
              </div>
            </div>
          </section>

          <section class="info_section">
            <h6 class="section_title">{{ $t("complaints.userInfo") }}</h6>
            <div class="people">
              <article>
                <span>{{ $t("complaints.clientName") }}</span>
                <strong>{{ personName(client) }}</strong>
                <p dir="ltr">{{ client && client.mobile }}</p>
                <p>{{ client && client.email }}</p>
              </article>
              <article>
                <span>{{ $t("complaints.providerName") }}</span>
                <strong>{{ personName(provider) }}</strong>
                <p dir="ltr">{{ provider && provider.mobile }}</p>
                <p>{{ provider && provider.email }}</p>
              </article>
            </div>
            <p class="opened_by" v-if="openedBy">
              {{ $t("complaints.openedBy") }}:
              {{ openedBy.name }}
              ({{
                openedBy.type === "provider"
                  ? $t("complaints.providerName")
                  : $t("complaints.clientName")
              }})
            </p>
          </section>

          <section class="info_section" v-if="attachments.length">
            <h6 class="section_title">{{ $t("complaints.attachments") }}</h6>
            <div class="attachments">
              <a
                v-for="file in attachments"
                :key="file.id || file.url"
                :href="fileUrl(file)"
                target="_blank"
                rel="noopener"
              >
                {{ file.name || fileUrl(file) || "#" + file.id }}
              </a>
            </div>
          </section>

          <section class="info_section" v-if="replies.length">
            <h6 class="section_title">{{ $t("complaints.replies") }}</h6>
            <div
              class="log_item"
              v-for="reply in replies"
              :key="reply.id || reply.created_at"
            >
              <strong>{{ reply.sender || (reply.user && reply.user.name) || "-" }}</strong>
              <p>{{ reply.message || reply.body || reply.note }}</p>
              <small>{{ reply.created_at }}</small>
            </div>
          </section>

          <section class="info_section" v-if="resolutionLog.length">
            <h6 class="section_title">{{ $t("complaints.resolutionLog") }}</h6>
            <div
              class="log_item"
              v-for="(action, index) in resolutionLog"
              :key="action.id || index"
            >
              <strong>{{ label(action.action) }}</strong>
              <p>
                {{ action.note || action.reason || "-" }}
                <span v-if="action.amount"> — {{ action.amount }}</span>
              </p>
              <small>{{ action.created_at }}</small>
            </div>
          </section>

          <section class="info_section">
            <h6 class="section_title">{{ $t("complaints.changeStatus") }}</h6>
            <div class="form_grid">
              <label class="field">
                <span>{{ $t("complaints.status") }}</span>
                <select v-model="selectedStatus">
                  <option value="">{{ $t("complaints.status") }}</option>
                  <option
                    v-for="item in statusOptions"
                    :key="item.id"
                    :value="item.id"
                  >
                    {{ item.name }}
                  </option>
                </select>
              </label>
              <label class="field" v-if="selectedStatus === 'rejected'">
                <span>{{ $t("complaints.reason") }}</span>
                <input type="text" v-model.trim="rejectReason" />
              </label>
            </div>
            <button
              class="action_btn"
              @click="updateComplaintStatus"
              :disabled="isUpdatingStatus"
            >
              {{ $t("complaints.changeStatus") }}
            </button>
          </section>

          <section class="info_section">
            <h6 class="section_title">{{ $t("complaints.resolutionAction") }}</h6>
            <div class="form_grid">
              <label class="field">
                <span>{{ $t("complaints.action") }}</span>
                <select v-model="resolution.action">
                  <option value="">{{ $t("complaints.action") }}</option>
                  <option
                    v-for="item in actionOptions"
                    :key="item.id"
                    :value="item.id"
                  >
                    {{ item.name }}
                  </option>
                </select>
              </label>
              <label class="field" v-if="needsUser">
                <span>{{ $t("complaints.party") }}</span>
                <select v-model="resolution.party">
                  <option value="">{{ $t("complaints.party") }}</option>
                  <option
                    v-for="item in partyOptions"
                    :key="item.id"
                    :value="item.id"
                  >
                    {{ item.name }}
                  </option>
                </select>
              </label>
              <label class="field" v-if="needsAmount">
                <span>{{ $t("complaints.amount") }}</span>
                <input type="number" min="0" step="0.01" v-model.trim="resolution.amount" />
              </label>
              <label class="field" v-if="needsPart">
                <span>{{ $t("complaints.part") }}</span>
                <select v-model="resolution.part">
                  <option value="">{{ $t("complaints.part") }}</option>
                  <option
                    v-for="item in partOptions"
                    :key="item.id"
                    :value="item.id"
                  >
                    {{ item.name }}
                  </option>
                </select>
                <small class="hint">{{ $t("complaints.adjustFirstHint") }}</small>
              </label>
              <label class="field span_2">
                <span>
                  {{ $t("complaints.note") }}
                  <span class="required_mark">*</span>
                </span>
                <textarea rows="3" v-model.trim="resolution.note" required></textarea>
                <small class="hint">{{ $t("complaints.noteRequired") }}</small>
              </label>
            </div>
            <button
              class="action_btn"
              :disabled="isSubmittingAction"
              @click="submitResolution"
            >
              {{ $t("complaints.submitAction") }}
            </button>
          </section>
        </div>
      </div>
    </template>
  </base-modal>
</template>

<script>
import BaseModal from "@/components/ui/BaseModal.vue";
import payoutLabels from "@/utils/payoutLabels";

export default {
  name: "ComplaintDetail",
  components: { BaseModal },
  props: {
    showModal: { type: Boolean, required: true },
    complaintData: { type: Object, default: null },
  },
  data() {
    return {
      detail: null,
      selectedStatus: "",
      rejectReason: "",
      isUpdatingStatus: false,
      isValidating: false,
      isSubmittingAction: false,
      resolution: {
        action: "",
        party: "",
        amount: "",
        part: "",
        note: "",
      },
    };
  },
  computed: {
    booking() {
      return this.detail?.booking || null;
    },
    client() {
      return this.booking?.client || this.detail?.client || null;
    },
    provider() {
      return this.booking?.provider || this.detail?.provider || null;
    },
    openedBy() {
      return this.detail?.opened_by || null;
    },
    attachments() {
      return this.detail?.attachments || [];
    },
    replies() {
      return this.detail?.replies || [];
    },
    resolutionLog() {
      return this.detail?.resolution_actions || [];
    },
    descriptionText() {
      const value = this.detail?.description;
      if (value && typeof value === "object") return JSON.stringify(value);
      return value || "-";
    },
    amountItems() {
      const amounts = this.booking?.amounts;
      if (!amounts || typeof amounts !== "object") return [];
      return Object.keys(amounts).map((key) => ({
        key,
        label: this.label(key),
        value: key === "currency" ? payoutLabels.currency(this, amounts[key]) : amounts[key],
      }));
    },
    payout() {
      return this.detail?.payout || null;
    },
    payoutReleases() {
      return this.payout?.releases || [];
    },
    payoutFacts() {
      if (!this.payout) return [];
      const fields = [
        ["hold_percent", "bookingPayouts.hold_percent"],
        ["order_total", "bookingPayouts.order_total"],
        ["commission_total", "bookingPayouts.commission_total"],
        ["net", "bookingPayouts.net"],
        ["cash_amount", "bookingPayouts.cash_amount"],
        ["first_amount", "bookingPayouts.first_amount"],
        ["second_amount", "bookingPayouts.second_amount"],
        ["finished_at", "bookingPayouts.finished_at"],
        ["available_at", "bookingPayouts.available_at"],
        ["auto_release_at", "bookingPayouts.auto_release_at"],
      ];
      return fields
        .filter(([key]) => {
          const value = this.payout[key];
          return value !== null && value !== undefined && value !== "";
        })
        .map(([key, label]) => ({
          label: this.$t(label),
          value: this.text(this.payout[key]),
        }));
    },
    partyOptions() {
      return [this.client, this.provider]
        .filter((person) => person && person.id)
        .map((person) => ({
          id: person.id,
          name: `${this.personRole(person)}: ${person.name}`,
        }));
    },
    actionId() {
      return this.resolution.action || "";
    },
    needsUser() {
      return ["credit_wallet", "debit_wallet"].includes(this.actionId);
    },
    needsAmount() {
      return ["credit_wallet", "debit_wallet", "adjust_payout"].includes(
        this.actionId
      );
    },
    needsPart() {
      return this.actionId === "adjust_payout";
    },
    statusOptions() {
      return [
        { id: "resolved", name: this.$t("complaints.resolved") },
        { id: "rejected", name: this.$t("complaints.rejected") },
      ];
    },
    actionOptions() {
      return [
        { id: "credit_wallet", name: this.$t("complaints.creditWallet") },
        { id: "debit_wallet", name: this.$t("complaints.debitWallet") },
        { id: "adjust_payout", name: this.$t("complaints.adjustPayout") },
        { id: "release_payout", name: this.$t("complaints.releasePayout") },
      ];
    },
    partOptions() {
      return [
        { id: "first", name: this.$t("complaints.firstPart") },
        { id: "second", name: this.$t("complaints.secondPart") },
      ];
    },
  },
  watch: {
    showModal(open) {
      if (open && this.complaintData?.id) this.loadDetail();
    },
    complaintData: {
      handler(value) {
        if (this.showModal && value?.id) this.loadDetail();
      },
      deep: true,
    },
  },
  methods: {
    text(value) {
      return value === null || value === undefined || value === "" ? "-" : String(value);
    },
    label(value) {
      if (value === null || value === undefined || value === "") return "-";
      const key = `complaints.${value}`;
      return this.$te(key) ? this.$t(key) : String(value);
    },
    payoutStatusLabel(value) {
      return payoutLabels.status(this, value);
    },
    partLabel(value) {
      return payoutLabels.part(this, value);
    },
    reasonLabel(value) {
      return payoutLabels.reason(this, value);
    },
    statusClass(status) {
      return {
        danger: status === "open" || status === "rejected",
        warning: status === "responded",
        success: status === "resolved" || status === "done",
      };
    },
    personName(person) {
      return person?.name || "-";
    },
    personRole(person) {
      if (this.client && person.id === this.client.id) {
        return this.$t("complaints.clientName");
      }
      return this.$t("complaints.providerName");
    },
    fileUrl(file) {
      return file.url || file.path || file.file || file.image || "";
    },
    async loadDetail() {
      try {
        const res = await this.$axios.get(
          `complaints/${this.complaintData.id}`
        );
        const payload = res.data.data || {};
        this.detail = payload.complaint || payload.Complaint || payload;
        this.selectedStatus = ["resolved", "rejected"].includes(
          this.detail.status
        )
          ? this.detail.status
          : "";
      } catch (error) {
        this.detail = this.complaintData;
        this.$message.error(error.response?.data?.message);
      }
    },
    async validateComplaint() {
      this.isValidating = true;
      try {
        await this.$axios.post(`complaints/${this.detail.id}/validate`, {});
        this.$message.success(this.$t("complaints.validateSuccess"));
        await this.loadDetail();
        this.$emit("refreshData");
      } catch (error) {
        this.$message.error(error.response?.data?.message);
      } finally {
        this.isValidating = false;
      }
    },
    async updateComplaintStatus() {
      if (!this.selectedStatus) return;
      this.isUpdatingStatus = true;
      const body = { status: this.selectedStatus };
      if (this.selectedStatus === "rejected") body.reason = this.rejectReason;
      try {
        await this.$axios.put(`complaints/status/${this.detail.id}`, body);
        this.$message.success(this.$t("complaints.statusUpdated"));
        this.$emit("refreshData");
        await this.loadDetail();
      } catch (error) {
        this.$message.error(error.response?.data?.message);
      } finally {
        this.isUpdatingStatus = false;
      }
    },
    async submitResolution() {
      if (!this.actionId || !this.resolution.note) return;
      if (this.needsUser && !this.resolution.party) return;
      const body = {
        action: this.actionId,
        note: this.resolution.note,
      };
      if (this.needsUser) body.user_id = Number(this.resolution.party);
      if (this.needsAmount) body.amount = Number(this.resolution.amount);
      if (this.needsPart) body.part = this.resolution.part;
      this.isSubmittingAction = true;
      try {
        await this.$axios.post(
          `complaints/${this.detail.id}/resolution-actions`,
          body
        );
        this.$message.success(this.$t("complaints.actionSuccess"));
        this.resolution = {
          action: "",
          party: "",
          amount: "",
          part: "",
          note: "",
        };
        await this.loadDetail();
      } catch (error) {
        this.$message.error(error.response?.data?.message);
      } finally {
        this.isSubmittingAction = false;
      }
    },
  },
};
</script>

<style scoped lang="scss">
.modal_card {
  background: #fff;
  width: 100%;
  max-height: min(90vh, 900px);
  display: flex;
  flex-direction: column;
  overflow: hidden;
  min-height: 0;
}

.modal_header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  flex-shrink: 0;
  gap: 12px;
  padding: 16px 20px;
  border-bottom: 1px solid #eee;
  background: #fff;

  h5 {
    margin: 0 0 8px;
    font-size: 18px;
    line-height: 1.3;
  }
}

.modal_body {
  overflow-y: auto;
  flex: 1 1 auto;
  min-height: 0;
  padding: 4px 20px 24px;
  -webkit-overflow-scrolling: touch;
}

.info_section {
  padding: 18px 0;
  border-bottom: 1px solid #f0f0f0;

  &:last-child {
    border-bottom: 0;
  }
}

.section_title {
  margin: 0 0 14px;
  font-size: 15px;
  font-weight: 700;
}

.facts,
.people,
.amount_grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 12px 16px;
}

.amount_grid {
  margin-top: 12px;
  grid-template-columns: repeat(3, minmax(0, 1fr));
}

.facts div,
.people article,
.amount_grid div {
  background: #fafafa;
  border: 1px solid #eee;
  border-radius: 10px;
  padding: 12px 14px;

  span {
    display: block;
    color: #777;
    font-size: 12px;
    margin-bottom: 4px;
  }

  strong,
  p {
    margin: 0;
    font-size: 14px;
    overflow-wrap: anywhere;
  }
}

.description,
.opened_by {
  margin: 12px 0 0;
}

.attachments {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;

  a {
    padding: 8px 12px;
    border-radius: 8px;
    background: #f6f6f6;
  }
}

.log_item {
  padding: 12px;
  border-radius: 10px;
  background: #fafafa;
  margin-bottom: 8px;

  p,
  small {
    display: block;
    margin: 4px 0 0;
  }

  a {
    display: inline-block;
    margin-top: 6px;
  }
}

.hint {
  display: block;
  margin-top: 6px;
  color: #777;
  font-size: 12px;
}

.required_mark {
  color: #c62828;
  margin-inline-start: 2px;
}

.payout_parts {
  margin-top: 12px;
}

.form_grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 12px 16px;
  align-items: start;
}

.field {
  display: flex;
  flex-direction: column;
  gap: 6px;
  min-width: 0;
  margin: 0;

  span {
    color: #666;
    font-size: 13px;
    font-weight: 600;
  }

  input,
  select,
  textarea {
    width: 100%;
    min-height: 42px;
    border: 1px solid #ddd;
    border-radius: 8px;
    background: #fff;
    padding: 8px 12px;
    font-size: 14px;
    color: #222;
    outline: none;
  }

  textarea {
    min-height: 88px;
    resize: vertical;
  }

  &.span_2 {
    grid-column: 1 / -1;
  }
}

.action_btn {
  margin-top: 8px;
  border: 0;
  border-radius: 8px;
  padding: 10px 18px;
  background: var(--main_theme_clr, #111);
  color: #fff;
  cursor: pointer;

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
}

.status_badge {
  display: inline-block;
  padding: 4px 10px;
  border-radius: 999px;
  font-size: 12px;
  font-weight: 700;
  background: #eee;

  &.danger {
    background: #fdecea;
    color: #c62828;
  }

  &.warning {
    background: #fff4e5;
    color: #ef6c00;
  }

  &.success {
    background: #e8f5e9;
    color: #2e7d32;
  }
}

.close_btn {
  background: none;
  border: 0;
  font-size: 20px;
  cursor: pointer;
}

@media (max-width: 900px) {
  .amount_grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}

@media (max-width: 700px) {
  .modal_card {
    max-height: 100%;
    border-radius: 0;
  }

  .modal_header,
  .modal_body {
    padding-left: 14px;
    padding-right: 14px;
  }

  .facts,
  .people,
  .amount_grid,
  .form_grid {
    grid-template-columns: 1fr;
  }

  .form_grid .span_2 {
    grid-column: auto;
  }

  .action_btn {
    width: 100%;
  }

  .attachments a {
    max-width: 100%;
    overflow-wrap: anywhere;
  }
}
</style>
